import React, { useEffect, useState } from "react";
import useImageUpload from "../../../hooks/useImageUpload";
import { IBanner } from "../../../interfaces/banner";
import { useForm } from "react-hook-form";
import { API_ENDPOINT, API_URL_2 } from "../../../api/constant";
import { bannerPageLink, showToastNotification } from "../../../utility/helper";
import { callApi } from "../../../api/callApi";
import Layout from "../../../components/Layout/Layout";
import ErrorMessage from "../../../components/UI/Notifications/ErrorMessage";
import Button from "../../../components/UI/Buttons/Button";
import SelectDropdown from "../../../components/UI/Forms/SelectDropdown";
import InputField from "../../../components/UI/Forms/InputField";
import useConfirmationModal from "../../../hooks/useConfirmationModal";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import Table from "../../../components/UI/CommonTable/CommonTable";
import useFileDelete from "../../../hooks/useFileDelete";
const Banner: React.FC = () => {
    const { uploadImage, error } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const [banners, setBanners] = useState<IBanner[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm({ mode: 'onBlur' });
    const [imageURL, setImageURL] = useState<{
        bannerImage: string,
        subBannerImage: string
    }>({ bannerImage: '', subBannerImage: '' });
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [editingBanner, setEditingBanner] = useState<IBanner | null>(null); // Track the banner being edited
    const { showModal, modalProps, confirmAction } = useConfirmationModal();

    const onSubmit = async (data: any) => {
        setLoadingButton(true);
        const postData = {
            image: imageURL,
            title: data?.title || '',
            page: data.page,
            bannerImage: imageURL.bannerImage,
            subBannerImage: imageURL.subBannerImage,
        };
        try {
            if (editingBanner) {
                // Update banner
                await callApi(API_URL_2 + API_ENDPOINT.page + `banners/${editingBanner.id}`, { method: 'PUT', data: postData });
                showToastNotification('success', 'Banner updated successfully');
            } else {
                // Create new banner
                await callApi(API_URL_2 + API_ENDPOINT.page + 'banners', { method: 'POST', data: postData });
                showToastNotification('success', 'Banner added successfully');
            }

            reset();
            setImageURL({ bannerImage: '', subBannerImage: '' });
            setEditingBanner(null);
            getBanners();
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoadingButton(false);
        }
    };

    useEffect(() => {
        getBanners();
    }, []);

    const getBanners = async () => {
        try {
            setLoading(true);
            const response = await callApi(API_URL_2 + API_ENDPOINT.page + 'banners', { method: 'GET' });
            setBanners(response.data);
        } catch (error: any) {
            console.error('Failed to fetch banners:', error.message);
        } finally {
            setLoading(false);
        }
    };

    const deleteBanner = async (id: string) => {
        try {
            await callApi(API_URL_2 + API_ENDPOINT.page + `banners/${id}`, { method: 'DELETE' });
            setBanners((prevBanners: IBanner[]) => prevBanners.filter((banner) => banner.id !== id));
        } catch (error: any) {
            console.error('Failed to delete banner:', error.message);
        }
    };

    const handleEditBanner = (banner: IBanner) => {
        setEditingBanner(banner);
        setValue('page', banner.page);
        setValue('title', banner.title);
        setImageURL({ bannerImage: banner.bannerImage, subBannerImage: banner.subBannerImage });

    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const uploadedUrl = await uploadImage(file);
            if (type === 'bannerImage') {
                setImageURL({ ...imageURL, bannerImage: uploadedUrl });
            }
            else if (type === 'subBannerImage') {
                setImageURL({ ...imageURL, subBannerImage: uploadedUrl });
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteImage = async (type: string, path: string) => {
        if (type === 'bannerImage') {
            setImageURL({ ...imageURL, bannerImage: '' });
        } else if (type === 'subBannerImage') {
            setImageURL({ ...imageURL, subBannerImage: '' });
        }
        await deleteFile(path);
    };


    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: 'Confirm Delete',
            message: 'Are you sure you want to delete this banner?',
            onConfirm: () => deleteBanner(id),
        });
    };

    const columns = [
        { header: "Page", accessor: "page" },
        { header: "Heading", accessor: "heading" },
        {
            header: "Banner Image", accessor: "bannerImage", render: (bannerImage: string) =>
                <div className="table-image">
                    <img
                        className="img-image"
                        src={bannerImage}
                        alt=""
                    />
                </div>,
        },
        {
            header: "Sub Banner Image", accessor: "subBannerImage",
            render: (bannerImage: string) =>
                <div className="table-image">
                    <img
                        className="img-image"
                        src={bannerImage}
                        alt=""
                    />
                </div>,
        },
    ];

    const actions = [
        {
            icon: "bi bi-pen",
            color: "success",
            onClick: (banner: IBanner) => handleEditBanner(banner),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            onClick: (banner: IBanner) => handleDeleteClick(banner.id),
        },
    ];

    return (
        <Layout>
            <h1 className="app-page-title">Banners</h1>
            <div className="row">
                <div className="col-lg-8">
                    <div className="app-card app-card-orders-table shadow-sm mb-4 p-4">
                        <div className="app-card-body">
                            <Table
                                columns={columns}
                                data={banners}
                                loading={loading}
                                actions={actions}
                                emptyMessage="No products available."
                            />
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="app-card app-card-settings shadow-sm p-4">
                        <div className="app-card-body">
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <h3> {editingBanner ? "Update Banner" : "Add Banner"} </h3>
                                <div className="row">
                                    <div className="mb-3 col-12">
                                        <SelectDropdown
                                            options={bannerPageLink}
                                            register={register("page", {
                                                required: "Page is required",
                                            })}
                                            name="page"
                                            label="Page"
                                        />
                                        <ErrorMessage message={errors.page?.message?.toString()} />
                                    </div>

                                    <div className="mb-3 col-12">
                                        <InputField
                                            type="text"
                                            register={register('title', { required: false })}
                                            name="title"
                                            label="Heading"
                                            placeholder="Enter Heading"
                                        />
                                        <ErrorMessage message={errors.title?.message?.toString()} />
                                    </div>
                                    <div className="mb-3 col-12 image-upload">
                                        <div className="form-label">
                                            Upload Banner
                                        </div>
                                        <label htmlFor="file-input" className="choosefilenew position-relative">
                                            <input
                                                type="file"
                                                id="file-input"
                                                accept="image/*"
                                                hidden
                                                onChange={(e) => handleFileChange(e, "bannerImage")}
                                            />
                                            <div>
                                                <div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>Upload Banner
                                            </div>

                                        </label>
                                        <div className="addimagefiles mt-2">
                                            {imageURL.bannerImage && (
                                                <div className="image-card">
                                                    <img
                                                        src={imageURL.bannerImage}
                                                        alt="Icon"
                                                        className="image-preview"
                                                    />
                                                    <button
                                                        className="btn btn-danger px-2 py-1 text-white position-absolute"
                                                        type="button"
                                                        onClick={() => handleDeleteImage("bannerImage", imageURL.bannerImage)}
                                                    >
                                                        <i className='bi bi-trash3'></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <ErrorMessage message={error?.toString()} />
                                    </div>
                                    <div className="mb-3 col-12 image-upload">
                                        <div className=" form-label">
                                            Upload Sub-Banner
                                        </div>
                                        <label htmlFor="file-input-23" className="choosefilenew position-relative">
                                            <input
                                                type="file"
                                                id="file-input-23"
                                                accept="image/*"
                                                hidden
                                                onChange={(e) => handleFileChange(e, "subBannerImage")}
                                            />
                                            <div>
                                                <div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>Upload Sub-Banner
                                            </div>

                                        </label>
                                        <div className="addimagefiles mt-2">
                                            {imageURL.subBannerImage && (
                                                <div className="image-card">
                                                    <img
                                                        src={imageURL.subBannerImage}
                                                        alt="Icon"
                                                        className="image-preview"
                                                    />
                                                    <button
                                                        className="btn btn-danger px-2 py-1 text-white position-absolute"
                                                        type="button"
                                                        onClick={() => handleDeleteImage("subBannerImage", imageURL.subBannerImage)}
                                                    >
                                                        <i className='bi bi-trash3'></i>

                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <ErrorMessage message={error?.toString()} />
                                    </div>
                                </div>
                                <div className="col-12 text-end mt-3 ">
                                    <Button
                                        loading={loadingButton}
                                        className="btn btn-primary text-white px-5"
                                        type="submit"
                                        text={editingBanner ? "Update" : "Save"}
                                    />
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default Banner;
